import { useState } from 'react'

import type { Batch, ModuleSnapshot, PrepareModulesReport } from '@ctcells/contract'
import { enumLabel, t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { Field } from '../../atoms/Field.tsx'
import { Modal } from '../../atoms/Modal.tsx'
import { ShortId } from '../../atoms/ShortId.tsx'
import { TextInput } from '../../atoms/TextInput.tsx'
import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import { SettingsRow } from '../../layout/SettingsList.tsx'
import type { ActionGate, ScreenAccess } from '../access.ts'
import { dateTimeText, orDash } from '../format.ts'

import { prepareRunGate } from './prepareModulesFlow.ts'

/**
 * 관리 › 설정 › 운영 — **배치 준비 강제 정리**(`POST /modules/prepare`).
 *
 * ## 🔴 강한 동작이다 — 되돌릴 수 없다
 *
 * 한 번의 호출로 **비종착 배치를 전부**(아직 시작도 안 한 `created` 를 포함해) 종착시키고
 * 모듈 넷을 유휴로 되돌린다. 히나 실측 2026-09-22: 「**방금 만든 배치도 닫히고 그 시료는
 * 재사용 불가가 된다**」. 그래서 랩웨어 보관·점유 해제와 **같은 꼴의 비가역 확인 대화**를 거친다:
 *
 * 1. `ValueRows` 로 **지금 닫힐 것**을 보인다 — `GET /batches?phase=active` 가 준 목록 그대로
 * 2. 되돌릴 모듈을 보인다 — `GET /modules` 중 **`service_state === 'active'`** 인 것
 *    (계약 원문: 「**서버가 목록에서 «거르지» 않는다 — 표지만 준다. 거르는 판단은 보는 쪽이
 *    한다**」). 수를 손으로 적지 않는다 — 「4대」라고 박으면 등록이 늘 때 거짓이 된다
 * 3. 「되돌리기 · 불가」
 * 4. **사유 입력 필수** — `IrreversibleCommandEnvelope.reason` 이 감사 원장에 그대로 봉인된다.
 *    🔴 프론트가 채우지 않는다. 채우면 원장이 거짓이 된다
 *
 * ## 🔴 결과는 «응답에 있는 값»만 글자로 적는다
 *
 * `ready` · 닫은 배치(`batches[]`) · 모듈(`modules[]`) · 사람 몫(`human_actions[]`) ·
 * 보고만 한 것(`notes[]`). ⚠ **`ready: false` 도 200 이고 실패가 아니다** — 「무엇이 남았는지」가
 * 본문에 있다(계약 원문). 빨갛게 칠해 실패로 읽히게 하지 마라.
 *
 * ## 게이트
 *
 * | 축 | 값 | 근거 |
 * | --- | --- | --- |
 * | 권한 | `control`(operator · admin) | `views/modules.py:1479` `require_roles(WRITE_ROLES)` |
 * | 제어락 | **필수** | 같은 줄 `require_control_lock` — 없으면 버튼이 「제어락 필요」로 선다 |
 * | 작업 | **종착 안 한 작업 0건** | 화면 게이트(2026-09-28) — 서버는 그 작업들을 전부 종착시킨다. 이름을 사유에 적는다 |
 *
 * 게이트 정의는 `prepareModulesFlow.ts` 한 곳이다 — 모듈 서랍의 「트레이 자리 정리」와 같다.
 *
 * 🔴 **숨기지 않는다.** 권한도 락도 없는 사람에게 **비활성 + 사유**로 보인다(CLAUDE.md RBAC).
 */
export interface PrepareModulesRowProps {
  access?: ScreenAccess
  /** 확인 대화가 열렸나. 🔴 **컨테이너가 쥔다** — 열렸을 때만 목록 두 개를 부르기 때문이다 */
  open?: boolean
  onOpen?: (() => void) | undefined
  onClose?: (() => void) | undefined
  /** 🔴 지금 닫힐 것. `GET /batches?phase=active` 가 준 것 그대로 — 화면이 거르지 않는다 */
  activeBatches?: readonly Batch[]
  activeBatchesLoading?: boolean
  /** 목록을 못 봤을 때의 사유. 「0건」과 갈린다 — 못 본 것을 「없다」로 접지 않는다 */
  activeBatchesNotice?: string | undefined
  /**
   * 🔴 **정리 «대상» 모듈이다 — 등록된 모듈 전부가 아니다.** 거르는 일은 컨테이너가 한다
   *    (`service_state === 'active'`). 서버도 같은 축으로 고른다
   *    (`module_prepare.module_targets` → `module_endpoints(service_state=SERVICE_ACTIVE)`).
   * ⚠ 걸러서 넘기지 않으면 **종착 선언된 옛 등록**까지 「되돌린다」고 적게 된다(실측 2026-09-22:
   *   `GET /modules` 8건 중 `mock-*` 넷이 `terminated` 인데 서버는 넷만 건드렸다).
   */
  targetModules?: readonly ModuleSnapshot[]
  modulesNotice?: string | undefined
  /** `undefined` 면 버튼이 비활성 + 사유다 */
  onPrepare?: ((reason: string) => void) | undefined
  preparing?: boolean
  error?: string | undefined
  /** 🔴 서버가 준 결과. 있으면 대화가 **결과 화면**으로 바뀐다 */
  result?: PrepareModulesReport | undefined
}

export function PrepareModulesRow({
  access = {},
  open = false,
  onOpen,
  onClose,
  activeBatches = [],
  activeBatchesLoading = false,
  activeBatchesNotice,
  onPrepare,
  preparing = false,
  ...dialog
}: PrepareModulesRowProps) {
  /*
   * 🔴 세 축 AND + 작업 0건으로 한 번만 계산한다 — 여는 버튼과 대화의 확인 버튼이 **같은 게이트**를 쓴다.
   * 🔴 `needsLock: true` — 서버가 `X-Control-Token` 을 요구한다. 락이 없으면 눌러도 409 라,
   *    그 사실을 **버튼이 미리 말한다**(막는 것이 아니라 알리는 것).
   */
  const runGate = prepareRunGate(
    access,
    { activeBatches, activeBatchesLoading, activeBatchesNotice },
    { preparing, canSend: onPrepare !== undefined },
  )

  return (
    <>
      <SettingsRow
        action={
          <Button onClick={() => onOpen?.()} size="pill" variant="danger" {...runGate}>
            {t('action.prepareModules')}
          </Button>
        }
        label={t('setting.prepareModules')}
        /* 🔴 값이 아니라 연산이라 값 자리가 없다 — 종전 「—」는 「값이 비었다」로 읽혔다(2026-09-28) */
        value={null}
      />
      {!open ? null : (
        <PrepareModulesDialog
          {...dialog}
          activeBatches={activeBatches}
          activeBatchesLoading={activeBatchesLoading}
          activeBatchesNotice={activeBatchesNotice}
          onClose={onClose}
          onPrepare={onPrepare}
          runGate={runGate}
        />
      )}
    </>
  )
}

export interface PrepareModulesDialogProps {
  title?: string | undefined
  activeBatches: readonly Batch[]
  activeBatchesLoading: boolean
  activeBatchesNotice?: string | undefined
  targetModules?: readonly ModuleSnapshot[] | undefined
  modulesNotice?: string | undefined
  /** 🔴 여는 버튼과 **같은** 게이트(`prepareRunGate`)를 받는다 */
  runGate: ActionGate
  onClose?: (() => void) | undefined
  onPrepare?: ((reason: string) => void) | undefined
  error?: string | undefined
  /** 있으면 대화가 결과 화면으로 바뀐다. 서랍은 결과를 카드에 적어서 넘기지 않는다 */
  result?: PrepareModulesReport | undefined
  /**
   * 잔여 위험 앞에 먼저 세울 한 줄 — 작업 줄 시작 창이 「이 작업도 중단됩니다 · 정리 뒤 작업을 새로 만들어야 합니다」를 싣는다(2026-10-23).
   * 안 주면 종전 그대로다
   */
  notice?: string | undefined
}

/**
 * 확인 대화 — 관리 › 설정과 모듈 서랍이 **같은 것을** 쓴다. 열렸을 때만 붙는다(사유 입력이 닫힐 때 사라진다).
 */
export function PrepareModulesDialog({
  title = t('setting.prepareModules'),
  activeBatches,
  activeBatchesLoading,
  activeBatchesNotice,
  targetModules = [],
  modulesNotice,
  runGate,
  onClose,
  onPrepare,
  error,
  result,
  notice,
}: PrepareModulesDialogProps) {
  const [reason, setReason] = useState('')
  /* 사유는 「모듈 준비」를 누를 때 잰다(eric 2026-10-27) — 단추는 `runGate`(권한 · 연결 · 진행 중 배치 · 전송 중)만 막는다 */
  const form = useSubmitValidation((value: string) => ({
    reason: value.trim() === '' ? t('validation.reasonRequired') : undefined,
  }))
  const reasonField = form.field('reason')
  const close = () => {
    setReason('')
    form.reset()
    onClose?.()
  }

  return (
    <Modal
      size="sm"
      footer={
        result === undefined ? (
          <>
            <Button onClick={close} size="pill">
              {t('action.cancel')}
            </Button>
            <Button
              size="pill"
              variant="danger"
              {...runGate}
              onClick={() => {
                if (runGate.disabled) return
                // 바닥 줄은 본문 밖이라 포커스 범위는 문서다 — 칸 id 는 이 창에만 있다
                if (form.validate(reason, document.body)) onPrepare?.(reason.trim())
              }}
            >
              {t('action.prepareModules')}
            </Button>
          </>
        ) : (
          <Button onClick={close} size="pill" variant="primary">
            {t('action.close')}
          </Button>
        )
      }
      onClose={close}
      title={title}
    >
      {result === undefined ? (
        <>
          <ValueRows
            rows={[
              {
                key: 'batches',
                label: t('row.prepareBatches'),
                value: activeBatchesLoading ? (
                  t('state.loading')
                ) : activeBatchesNotice !== undefined ? (
                  /* 🔴 「못 읽었다」를 「0건」으로 접지 않는다 — 사유를 그 자리에 적는다 */
                  <span className="deny">{activeBatchesNotice}</span>
                ) : activeBatches.length === 0 ? (
                  t('state.empty.activeBatches')
                ) : (
                  <ul className="plainlist">
                    {activeBatches.map((batch) => (
                      <li key={batch.batch_id}>
                        <ShortId value={batch.batch_id} />{' '}
                        <span className="muted">{enumLabel('enum.batchStatus', batch.status)}</span>
                      </li>
                    ))}
                  </ul>
                ),
              },
              {
                key: 'modules',
                label: t('row.prepareModules'),
                value:
                  modulesNotice !== undefined ? (
                    <span className="deny">{modulesNotice}</span>
                  ) : targetModules.length === 0 ? (
                    orDash(null)
                  ) : (
                    <ul className="plainlist">
                      {targetModules.map((module) => (
                        <li className="mono" key={module.module_id}>
                          {module.module_id}
                        </li>
                      ))}
                    </ul>
                  ),
              },
              {
                key: 'reversible',
                label: t('row.reversible'),
                value: t('value.irreversible'),
              },
            ]}
          />
          {/*
            🔴 **잔여 위험을 대화가 말한다.** 실측(2026-09-22)에서 **아직 시작도 안 한 배치**가
               같이 닫히고 그 시료가 재사용 불가가 됐다. 「전부」라는 말만으로는 그것을 모른다.
            ⚠ `role="alert"` — 색만으로 위험을 말하지 않는다(접근성).
          */}
          {notice === undefined ? null : (
            <p className="deny" role="alert">
              {notice}
            </p>
          )}
          <p className="deny" role="alert">
            {t('prepare.residualRisk')} {t('prepare.clearsHandoff')}
          </p>
          <Field label={t('field.reason')} required {...reasonField.fieldProps}>
            <TextInput
              {...reasonField.inputProps}
              onChange={(value) => {
                setReason(value)
                form.change('reason', value)
              }}
              value={reason}
              wide
            />
          </Field>
          {error === undefined ? null : (
            <p className="vissue" role="alert">
              {error}
            </p>
          )}
        </>
      ) : (
        <PrepareResult result={result} />
      )}
    </Modal>
  )
}

/**
 * 결과 — **응답에 있는 값만** 글자로 적는다.
 *
 * 🔴 **`ready: false` 를 실패로 그리지 않는다.** 200 이고, 무엇이 남았는지가 아래에 있다.
 * ⚠ `ready` 는 **지금 미러 기준**이라 방금 보낸 모듈 명령의 반영은 다음 갱신 뒤다(계약 원문).
 */
export function PrepareResult({ result }: { result: PrepareModulesReport }) {
  return (
    <>
      <ValueRows
        rows={[
          {
            key: 'ready',
            label: t('row.prepareReady'),
            /*
             * ⚠ **`ready` 는 «지금 미러 기준»이다**(계약 원문) — 방금 보낸 모듈 명령의 반영은
             *   다음 갱신 뒤다. 그래서 판정 시각(`ready_as_of`)을 같이 적는다. 응답에 있는 값이다.
             */
            value: (
              <>
                {result.ready ? t('value.prepareReady') : t('value.prepareNotReady')}
                {typeof result.ready_as_of === 'string' ? (
                  <span className="muted mono"> {dateTimeText(result.ready_as_of)}</span>
                ) : null}
              </>
            ),
          },
          {
            key: 'batches',
            label: t('row.prepareClosed'),
            value:
              result.batches.length === 0 ? (
                t('state.empty.activeBatches')
              ) : (
                <ul className="plainlist">
                  {result.batches.map((batch) => (
                    <li key={batch.batch_id ?? ''}>
                      <ShortId value={batch.batch_id ?? ''} />{' '}
                      <span className="muted">{orDash(batch.after_status)}</span>
                    </li>
                  ))}
                </ul>
              ),
          },
          {
            key: 'modules',
            label: t('row.prepareModules'),
            value: (
              <ul className="plainlist">
                {result.modules.map((module) => (
                  <li key={module.module_id ?? ''}>
                    <span className="mono">{module.module_id}</span>{' '}
                    <span className="muted">{orDash(module.after_state)}</span>
                    {(module.blocked_by ?? []).length === 0 ? null : (
                      <span className="deny"> {(module.blocked_by ?? []).join(', ')}</span>
                    )}
                  </li>
                ))}
              </ul>
            ),
          },
        ]}
      />

      {/*
        🔴 **사람이 해야 하는 것.** 소프트웨어가 실행하지 «않은» 갈래다(시약 보충) —
           자동 복구 금지(CLAUDE.md §4). 서버가 준 문면을 그대로 옮긴다.
      */}
      {result.human_actions.length === 0 ? null : (
        <ul className="plainlist deny" role="alert">
          {result.human_actions.map((action, index) => (
            <li key={`${action.module_id ?? ''}:${action.code ?? ''}:${index}`}>
              {action.what ?? action.code ?? ''}
            </li>
          ))}
        </ul>
      )}

      {/*
        실행하지 않고 «보고만» 한 것 — 시료 위치 잔재 등.
        🔴 **한 줄로 이어 붙이지 않는다**(2026-09-22 실물 확인). 서버가 주는 문장이 길어
           이어 붙이면 어디서 끊기는지 안 보인다. 줄마다 세운다 — 글자는 그대로다.
      */}
      {(result.notes ?? []).length === 0 ? null : (
        <ul className="plainlist muted">
          {(result.notes ?? []).map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      )}
    </>
  )
}
